import type { FC } from 'react';

import FormField from '@/common-components/FormField';
import Input from '@/common-components/Input';
import Pill from '@/common-components/Pill';
import type { UseAdminAccountsResult } from '@/features/settings/hooks/useAdminAccounts.hook';
import {
  ADMIN_ACCOUNT_ROLE_LABELS,
  type AdminAccountStatus,
} from '@/types/adminAccount.types';
import { formatDate } from '@/utils/date.util';

interface AdminRolesPanelProps {
  vm: UseAdminAccountsResult;
  /** The signed-in admin — their own row cannot be edited, matching the server's rule. */
  currentAdminId: string | undefined;
}

const STATUS_TONE: Record<AdminAccountStatus, 'success' | 'warn' | 'danger'> = {
  active: 'success',
  invited: 'warn',
  suspended: 'danger',
};

const STATUS_LABEL: Record<AdminAccountStatus, string> = {
  active: 'Active',
  invited: 'Invite pending',
  suspended: 'Suspended',
};

const AdminRolesPanel: FC<AdminRolesPanelProps> = ({ vm, currentAdminId }) => (
  <section className="rounded-2xl border border-[var(--color-divider)] bg-white p-7 shadow-[var(--shadow-card)]">
    <header className="mb-1 flex flex-wrap items-center justify-between gap-3">
      <h2 className="text-[13px] font-extrabold text-[var(--color-ink)]">👥 Admin Roles</h2>
      <button
        type="button"
        onClick={vm.onOpenInvite}
        className="rounded-lg bg-brand-500 px-3.5 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-600"
      >
        + Invite admin
      </button>
    </header>
    <p className="mb-4 text-[12px] text-[var(--color-ink-muted)]">
      Invited admins choose their own password from an emailed link and cannot sign in until they
      do. Every admin has full access. You cannot change your own status.
    </p>

    {vm.inviteSuccess ? (
      <p className="mb-3 rounded-lg bg-[var(--color-success-bg)] px-3 py-2 text-[12.5px] font-semibold text-[var(--color-success-ink)]">
        {vm.inviteSuccess}
      </p>
    ) : null}
    {vm.updateError ? (
      <p className="mb-3 rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-[12.5px] font-semibold text-[var(--color-danger-ink)]">
        {vm.updateError}
      </p>
    ) : null}

    {vm.isInviteOpen ? (
      <div className="mb-5 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-muted)] p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <FormField label="Name" htmlFor="invite-name" required error={vm.inviteErrors.name}>
            <Input
              id="invite-name"
              value={vm.inviteForm.name}
              onChange={(e) => vm.onChangeInvite('name', e.target.value)}
              placeholder="Priya Menon"
            />
          </FormField>
          <FormField label="Email" htmlFor="invite-email" required error={vm.inviteErrors.email}>
            <Input
              id="invite-email"
              type="email"
              value={vm.inviteForm.email}
              onChange={(e) => vm.onChangeInvite('email', e.target.value)}
              placeholder="priya@shoutt.live"
            />
          </FormField>
        </div>

        {vm.inviteError ? (
          <p className="mt-2 text-[12.5px] text-[var(--color-danger-ink)]">{vm.inviteError}</p>
        ) : null}

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={vm.onSubmitInvite}
            disabled={vm.isInviting}
            className="rounded-lg bg-brand-500 px-4 py-2 text-[12.5px] font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60"
          >
            {vm.isInviting ? 'Sending…' : 'Send invitation'}
          </button>
          <button
            type="button"
            onClick={vm.onCloseInvite}
            className="rounded-lg px-3 py-2 text-[12.5px] font-semibold text-[var(--color-ink-muted)] hover:bg-white"
          >
            Cancel
          </button>
        </div>
      </div>
    ) : null}

    {vm.isLoading ? (
      <p className="py-6 text-center text-[13px] text-[var(--color-ink-muted)]">Loading admins…</p>
    ) : vm.isError ? (
      <p className="py-6 text-center text-[13px] text-[var(--color-danger-ink)]">
        {vm.errorMessage ?? 'Could not load admins.'}
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] border-collapse">
          <thead>
            <tr className="border-b border-[var(--color-divider)]">
              {['Name', 'Status', 'Role', 'Last sign-in', ''].map((heading) => (
                <th
                  key={heading}
                  className="px-2 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.4px] text-[var(--color-ink-muted)]"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vm.admins.map((admin) => {
              const isSelf = admin.id === currentAdminId;
              const busy = vm.updatingId === admin.id;
              return (
                <tr key={admin.id} className="border-b border-[var(--color-divider)] last:border-b-0">
                  <td className="px-2 py-3">
                    <div className="text-[13px] font-semibold text-[var(--color-ink)]">
                      {admin.name}
                      {isSelf ? (
                        <span className="ml-1.5 text-[11px] font-medium text-[var(--color-ink-soft)]">
                          (you)
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[12px] text-[var(--color-ink-muted)]">{admin.email}</div>
                  </td>
                  <td className="px-2 py-3">
                    <Pill tone={STATUS_TONE[admin.status]}>{STATUS_LABEL[admin.status]}</Pill>
                  </td>
                  <td className="px-2 py-3 text-[12.5px] text-[var(--color-ink)]">
                    {ADMIN_ACCOUNT_ROLE_LABELS[admin.role]}
                  </td>
                  <td className="px-2 py-3 text-[12.5px] text-[var(--color-ink-muted)]">
                    {admin.lastLoginAt ? formatDate(admin.lastLoginAt) : 'Never'}
                  </td>
                  <td className="px-2 py-3 text-right">
                    {admin.status === 'invited' ? (
                      // Resending mints a fresh token and invalidates the previous link, so the
                      // confirmation says so rather than implying the old email still works.
                      <button
                        type="button"
                        onClick={() => vm.onResendInvite(admin)}
                        disabled={busy}
                        className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[var(--color-brand)] transition-colors hover:bg-[var(--color-surface-muted-2)] disabled:opacity-50"
                      >
                        {busy
                          ? 'Sending…'
                          : vm.resentInviteId === admin.id
                            ? 'Invite re-sent'
                            : 'Resend invite'}
                      </button>
                    ) : isSelf ? null : (
                      <button
                        type="button"
                        onClick={() => vm.onToggleSuspended(admin)}
                        disabled={busy}
                        className="rounded-lg px-3 py-1.5 text-[12px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)] disabled:opacity-50"
                      >
                        {busy ? '…' : admin.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

export default AdminRolesPanel;
